import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { matchPath } from 'react-router-dom';
import { clearGroupByVulns, newGetVulns, setGroupByVulns, setPage } from 'store/Manage/filterActions';
import { deleteSelectedVulns, hideVulnModalDelete, redirectToVulnDetail, showVulnModalDelete, showVulnerabilityDetail } from 'store/Manage/actions';
import { selectPage, selectRowsPerPage, selectGroupByField, selectGroupBy } from 'store/Filters/selectors';
import {
  selectVulnsSelected,
  selectShowDetail,
  selectSelectAll,
  selectVulnsCount,
  selectShowDeleteConfirmation,
  selectIsFetching,
  selectVulnsList
} from 'store/Manage/selectors';
import { Wrapper, LeftGroup, RightGroup, Separator, Group, ManualGroupingIcon, QuickPlannerIcon } from 'Common/Components/CustomActionBar/styled';
import Pagination from 'Common/Components/Pagination';
import CreateActions from 'Screens/Contextualization/Manage/components/CreateActions';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import GroupFieldSelector from '../GroupFieldSelector';
import FilterSideBarIcon from '../FilterSidebarIcon';
import TagButton from '../TagButton';
import RefreshButton from '../RefreshButton';
import AddRemoveColumnsButton from '../AddRemoveColumnsButton';
import { EEFeatureSlot } from 'Common/ee/EEFeatureSlot';
import EELockedFeature from 'Common/Components/EELockedFeature';
import { MODAL_UPGRADE_LICENSE_QUICK_PLANNER, MODAL_UPGRADE_LICENSE_MANUAL_GROUPING } from 'store/modals/modals';
import FFPlanner from 'Images/ff_planner.png';
import upgradeLicenseImage from 'Images/workspaces-license.png';
import omit from 'lodash/omit';
import { selectRiskScoreFeatureFlag, selectIsAdmin } from 'store/Faraday/selectors';
import CopyLink from '../CopyLink';
import CopyApiLink from '../CopyApiLink';
import Severity from '../Severity';
import Status from '../Status';
import AddNewEvidence from '../AddNewEvidence';
import SaveAsTemplate from '../SaveAsTemplate';
import ApplyTemplate from '../ApplyTemplate';
import { selectManageFields } from 'store/Preferences/manage/contextSelectors';
import { resetDefault, setFieldVisibility, setNewOrderColumns } from 'store/Preferences/vulnsGeneral/actions';
import ExportCSV from '../ExportCSV';
import { selectPathname } from 'store/Router/selectors';
import {
  selectExploreGroupActiveTab,
  selectExploreGroupHostsCount,
  selectExploreGroupHostsPage,
  selectExploreGroupServicesCount,
  selectExploreGroupServicesPage,
  selectIsExploreGroupOpen,
  selectExploreGroupSelectedHostIds,
  selectExploreGroupSelectedServiceIds
} from 'store/GroupVulns/selectors';
import {
  refreshExploreGroupHosts,
  refreshExploreGroupServices,
  setExploreGroupHostsPage,
  setExploreGroupServicesPage,
  deleteExploreGroupSelectedHosts,
  deleteExploreGroupSelectedServices
} from 'store/GroupVulns/actions';
import {
  showHostDetailById,
  loadServiceDetail
} from 'store/Contextualization/AssetDetail/actions';

const ActionBar = ({ entity, showDuplicateModal, setFF }) => {
  const dispatch = useDispatch();
  const vulnsSelected = useSelector(selectVulnsSelected);
  const vulnsCount = useSelector(selectVulnsCount);
  const vulnsList = useSelector(selectVulnsList);
  const showVulnDetail = useSelector(selectShowDetail);
  const page = useSelector((state) => selectPage('vulns', state));
  const rowsPerPage = useSelector((state) => selectRowsPerPage('vulns', state));
  const isGrouping = useSelector((state) => selectGroupByField('vulns', state));
  const isAdmin = useSelector(selectIsAdmin);
  const hasVulns = vulnsCount > 0;
  const selectAll = useSelector(selectSelectAll);
  const fields = useSelector(selectManageFields);
  const isAllowedByLicense = useSelector(selectRiskScoreFeatureFlag);
  // 'risk'/'cvss' are already blocked from actually rendering in the table (see
  // Columns.jsx), but the Edit Columns picker reads straight from `fields` and doesn't
  // know about the license -- drop them here too so community can't toggle a column that
  // will never show up.
  const editColumnsFields = isAllowedByLicense ? fields : omit(fields, ['risk', 'cvss']);
  const showDeleteConfirmation = useSelector(selectShowDeleteConfirmation);
  const isFetching = useSelector(selectIsFetching);
  const groupBy = useSelector((state) => selectGroupBy('vulns', state));
  const pathname = useSelector(selectPathname);
  const isExploreGroupOpen = useSelector(selectIsExploreGroupOpen);
  const exploreActiveTab = useSelector(selectExploreGroupActiveTab);
  const exploreHostsCount = useSelector(selectExploreGroupHostsCount);
  const exploreServicesCount = useSelector(selectExploreGroupServicesCount);
  const exploreHostsPage = useSelector(selectExploreGroupHostsPage);
  const exploreServicesPage = useSelector(selectExploreGroupServicesPage);
  const selectedHostIds = useSelector(selectExploreGroupSelectedHostIds);
  const selectedServiceIds = useSelector(selectExploreGroupSelectedServiceIds);
  const isExploreRoute = Boolean(matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  }));
  const isExploreContext = isExploreRoute || isExploreGroupOpen;
  const isExploreAssetsTab = isExploreContext && exploreActiveTab === 1;
  const isExploreServicesTab = isExploreContext && exploreActiveTab === 2;
  const isExploreAssetsOrServicesTab = isExploreContext && (exploreActiveTab === 1 || exploreActiveTab === 2);
  const showOnlyBasicExploreActions = isExploreAssetsOrServicesTab;
  let hasItems = hasVulns;
  if (isExploreAssetsTab) hasItems = exploreHostsCount > 0;
  else if (isExploreServicesTab) hasItems = exploreServicesCount > 0;
  const showCreateActions = !isExploreContext;
  const showVulnOnlyExcludedInExplore = !isExploreContext;
  const updateFieldVisibility = async (fieldObj, value, type) => {
    await dispatch(setFieldVisibility(fieldObj, value, type));
    dispatch(newGetVulns());
  };
  const resetColumns = async () => {
    await dispatch(resetDefault());
    dispatch(newGetVulns());
  };
  let paginationPage = page;
  let paginationTotal = vulnsCount;

  if (isExploreAssetsTab) {
    paginationPage = exploreHostsPage;
    paginationTotal = exploreHostsCount;
  } else if (isExploreServicesTab) {
    paginationPage = exploreServicesPage;
    paginationTotal = exploreServicesCount;
  }

  const shouldHidePagination = isExploreAssetsTab || isExploreServicesTab
    ? paginationTotal <= 0
    : (!hasVulns || isGrouping);
  const onPrev = () => {
    if (isExploreAssetsTab) {
      const prevPage = Math.max(exploreHostsPage - 1, 1);
      if (prevPage === exploreHostsPage) return;
      dispatch(setExploreGroupHostsPage(prevPage));
      dispatch(refreshExploreGroupHosts({ page: prevPage, pageSize: rowsPerPage }));
      return;
    }

    if (isExploreServicesTab) {
      const prevPage = Math.max(exploreServicesPage - 1, 1);
      if (prevPage === exploreServicesPage) return;
      dispatch(setExploreGroupServicesPage(prevPage));
      dispatch(refreshExploreGroupServices({ page: prevPage, pageSize: rowsPerPage }));
      return;
    }

    dispatch(setPage(page - 1));
  };
  const onNext = () => {
    if (isExploreAssetsTab) {
      const nextPage = exploreHostsPage + 1;
      dispatch(setExploreGroupHostsPage(nextPage));
      dispatch(refreshExploreGroupHosts({ page: nextPage, pageSize: rowsPerPage }));
      return;
    }

    if (isExploreServicesTab) {
      const nextPage = exploreServicesPage + 1;
      dispatch(setExploreGroupServicesPage(nextPage));
      dispatch(refreshExploreGroupServices({ page: nextPage, pageSize: rowsPerPage }));
      return;
    }

    dispatch(setPage(page + 1));
  };

  return (
    <Wrapper data-tour="Toolbar Vulnerabilities">
      { showDeleteConfirmation
        ? <DeleteConfirmationModal
            showDeleteConfirmation={ showDeleteConfirmation }
            selectAll={ selectAll }
            isFetching={ isFetching }
            entity={ entity }
            vulnsSelected={ vulnsSelected }
            vulnsList={ vulnsList }
            deleteVulns={ () => dispatch(deleteSelectedVulns()) }
            hideModalDelete={ () => dispatch(hideVulnModalDelete()) }
            vulnsCount={ vulnsCount }
        />
        : null
      }
      <LeftGroup>
        {showCreateActions && (
          <>
            <CreateActions entity={ entity } />
            <Separator />
          </>
        )}
        <Group>
          <EditButton
            hasVulns={ hasVulns }
            vulnsSelected={ vulnsSelected }
            openVulnDetail={ (vulnId) => dispatch(redirectToVulnDetail(vulnId)) }
            isExploreAssetsTab={ isExploreAssetsTab }
            isExploreServicesTab={ isExploreServicesTab }
            selectedHostIds={ selectedHostIds }
            selectedServiceIds={ selectedServiceIds }
            openHostDetail={ (hostId) => dispatch(showHostDetailById(hostId)) }
            openServiceDetail={ (serviceId) => dispatch(loadServiceDetail(serviceId)) }
          />
          {!selectAll && <TagButton
            setFF={ setFF }
            hasItems={ hasItems }
            vulnsSelected={ vulnsSelected }
            tagsEntity="menu"
            isExploreAssetsTab={ isExploreAssetsTab }
            isExploreServicesTab={ isExploreServicesTab }
            selectedHostIds={ selectedHostIds }
            selectedServiceIds={ selectedServiceIds }
          />}
          {!showOnlyBasicExploreActions && <CopyLink hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } />}
          {!showOnlyBasicExploreActions && <CopyApiLink hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />}
          {!showOnlyBasicExploreActions && <Severity hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />}
          {!showOnlyBasicExploreActions && <Status hasVulns={ hasVulns } groupBy={ isGrouping } vulnsSelected={ vulnsSelected } entity={ entity } />}
          {!showOnlyBasicExploreActions && <AddNewEvidence hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } openEvidenceTab={ (vulnId) => dispatch(showVulnerabilityDetail(vulnId, 'evidence')) } entity={ entity } />}
          {showVulnOnlyExcludedInExplore && <SaveAsTemplate hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />}
          {!showOnlyBasicExploreActions && <ApplyTemplate hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } />}
          <DeleteButton
            hasVulns={ hasItems }
            vulnsSelected={ vulnsSelected }
            showVulnDetail={ showVulnDetail }
            showModalDelete={ () => dispatch(showVulnModalDelete()) }
            entity={ entity }
            isExploreAssetsTab={ isExploreAssetsTab }
            isExploreServicesTab={ isExploreServicesTab }
            selectedHostIds={ selectedHostIds }
            selectedServiceIds={ selectedServiceIds }
            deleteExploreHosts={ () => dispatch(deleteExploreGroupSelectedHosts()) }
            deleteExploreServices={ () => dispatch(deleteExploreGroupSelectedServices()) }
          />
          {showVulnOnlyExcludedInExplore && (
            <EEFeatureSlot
              featureKey="manual-grouping"
              render={ (ManualGroupingButton) => <ManualGroupingButton hasVulns={ hasVulns } vulnsSelected={ vulnsSelected } entity={ entity } /> }
              fallback={ <EELockedFeature modalId={ MODAL_UPGRADE_LICENSE_MANUAL_GROUPING } image={ upgradeLicenseImage } descriptionIntlId="upgradeLicense.sidebar.manualGrouping.description" title="Manual Grouping" icon={ ManualGroupingIcon } /> }
            />
          )}
        </Group>
        { isAdmin && !showOnlyBasicExploreActions &&
          <Group>
            <Separator />
            <EEFeatureSlot
              featureKey="quick-planner-button"
              render={ (QuickPlannerButton) => <QuickPlannerButton hasVulns={ hasVulns } entity={ entity } /> }
              fallback={ <EELockedFeature modalId={ MODAL_UPGRADE_LICENSE_QUICK_PLANNER } image={ FFPlanner } descriptionIntlId="upgradeLicense.sidebar.planner.description" title="Quick Planner" icon={ QuickPlannerIcon } /> }
            />
          </Group>
        }
      </LeftGroup>

      <RightGroup>

        { hasVulns && showVulnOnlyExcludedInExplore && <GroupFieldSelector groupBy={ groupBy } setGroupBy={ (groupBy) => dispatch(setGroupByVulns(groupBy)) } clearGroupBy={ () => dispatch(clearGroupByVulns()) } /> }

        { <Pagination offset={ shouldHidePagination ? null : paginationPage } limit={ rowsPerPage } total={ paginationTotal } onPrev={ onPrev } onNext={ onNext } />}

        { hasVulns && !showOnlyBasicExploreActions && <Separator /> }

        {!showOnlyBasicExploreActions && (
          <Group>
            { hasVulns && <ExportCSV /> }
            <RefreshButton entity={ entity } />
            <FilterSideBarIcon entity="vulns" />
            <AddRemoveColumnsButton
              fields={ editColumnsFields }
              setFieldVisibility={ updateFieldVisibility }
              setNewOrderColumns={ (columns) => dispatch(setNewOrderColumns(columns)) }
              resetDefault={ resetColumns }
            />
          </Group>
        )}
      </RightGroup>
    </Wrapper>
  );
};

export default ActionBar;
